/** Assinatura eletrônica (Clicksign) de um documento PDF. */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Badge, ErrorBox, Field, FormGrid, Modal, useAct } from "./kit";
import { confirmar } from "./feedback";

interface SigReq { id: string; status: string; signers: Array<{ name: string; email: string }>; error: string | null; createdAt: string; completedAt: string | null }
const TONE: Record<string, "ok" | "warn" | "bad" | "muted" | "info"> = { enviado: "info", assinado: "ok", cancelado: "muted", erro: "bad", rascunho: "muted" };

export function SignatureModal({ doc, onClose }: { doc: { id: string; name: string }; onClose: () => void }): JSX.Element {
  const { data } = useQuery({ queryKey: ["assinaturas", doc.id], queryFn: () => api.get<SigReq[]>(`/documentos/${doc.id}/assinaturas`) });
  const [signers, setSigners] = useState([{ name: "", email: "", phone: "" }]);
  const send = useAct(() => api.post(`/documentos/${doc.id}/assinatura`, { signers: signers.map((s) => ({ ...s, phone: s.phone || null })) }), [["assinaturas", doc.id]], () => setSigners([{ name: "", email: "", phone: "" }]), "Enviado para assinatura");
  const cancel = useAct((id: string) => api.post(`/assinaturas/${id}/cancelar`), [["assinaturas", doc.id]], undefined, "Pedido cancelado");
  const ok = signers.every((s) => s.name.trim().length >= 2 && /.+@.+\..+/.test(s.email));
  return (
    <Modal title={`Assinatura eletrônica — ${doc.name}`} onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Fechar</button><button className="btn primary" disabled={!ok || send.isPending} onClick={() => send.mutate()}>{send.isPending ? "Enviando…" : "Enviar para assinar"}</button></>}>
      <div className="stack gap-12">
        {(data ?? []).map((r) => (
          <div key={r.id} className="lrow">
            <Badge tone={TONE[r.status] ?? "muted"}>{r.status}</Badge>
            <span className="grow small">{r.signers.map((s) => s.name).join(", ")}<div className="muted">{fmtDateTime(r.createdAt)}{r.error ? ` · ${r.error}` : ""}</div></span>
            {r.status === "enviado" && <button className="btn small" onClick={() => void confirmar("Cancelar este pedido de assinatura?").then((y) => y && cancel.mutate(r.id))}>Cancelar</button>}
          </div>
        ))}
        <div className="muted small">Os signatários recebem o link por e-mail. Quando todos assinarem, o PDF assinado entra aqui como nova versão.</div>
        {signers.map((s, i) => (
          <FormGrid key={i} cols={3}>
            <Field label="Nome" required><input className="input" value={s.name} onChange={(e) => setSigners(signers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} /></Field>
            <Field label="E-mail" required><input className="input" type="email" value={s.email} onChange={(e) => setSigners(signers.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} /></Field>
            <Field label="Celular"><input className="input" value={s.phone} onChange={(e) => setSigners(signers.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} /></Field>
          </FormGrid>
        ))}
        <div className="row">
          <button className="btn small" disabled={signers.length >= 10} onClick={() => setSigners([...signers, { name: "", email: "", phone: "" }])}>+ Signatário</button>
          {signers.length > 1 && <button className="btn small" onClick={() => setSigners(signers.slice(0, -1))}>Remover último</button>}
        </div>
        <ErrorBox error={send.error ?? cancel.error} />
      </div>
    </Modal>
  );
}
